import React from 'react';
import dayjs from 'dayjs';
import type { CAPARecord } from '@/services/quality.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

export type CAPAPrintCompanyInfo = {
  name: string;
  legalName?: string;
  address: string;
  logo?: string;
};

export type CAPAPrintViewProps = {
  record: CAPARecord & { ncr?: { ncr_number?: string; description?: string; disposition?: string } };
  companyInfo: CAPAPrintCompanyInfo;
};

function dash(value?: string | number | null | boolean): string {
  if (value == null || String(value).trim() === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  return dayjs(value).format('DD MMM YYYY');
}

export function CAPAPrintView({ record, companyInfo }: CAPAPrintViewProps) {
  const steps = Array.isArray(record.action_steps) ? record.action_steps : [];

  const metaLines = [
    { label: 'CAPA Number', value: dash(record.capa_number) },
    { label: 'Status', value: dash(record.status) },
    { label: 'Type', value: dash(record.type) },
    { label: 'Source', value: dash(record.source) },
    { label: 'Source Ref', value: dash(record.source_reference) },
    { label: 'Department', value: dash(record.department_name || record.department_id) },
    { label: 'Linked NCR', value: dash(record.ncr?.ncr_number || record.ncr_id) },
    {
      label: 'Responsible',
      value: dash(record.responsible_person_name || record.responsible_person_id),
    },
    { label: 'Target', value: formatDate(record.target_completion_date) },
    { label: 'Actual completion', value: formatDate(record.actual_completion_date) },
    { label: 'Progress', value: `${record.progress_percentage ?? 0}%` },
  ].filter((m) => m.value && m.value !== '—');

  const narrativeLines = [
    {
      lineNumber: 1,
      description: 'Description',
      cells: { value: dash(record.description) },
    },
    {
      lineNumber: 2,
      description: 'Problem statement',
      cells: { value: dash(record.problem_statement) },
    },
    {
      lineNumber: 3,
      description: 'Action plan',
      cells: { value: dash(record.action_plan) },
    },
    {
      lineNumber: 4,
      description: 'Action steps',
      cells: {
        value: steps.length
          ? steps
              .map(
                (s: any, i: number) =>
                  `${i + 1}. ${s.step || s.description || 'Step'}${s.done ? ' [done]' : ''}`
              )
              .join('\n')
          : '—',
      },
    },
    {
      lineNumber: 5,
      description: 'Effectiveness',
      cells: {
        value: [
          record.effectiveness_criteria
            ? `Criteria: ${record.effectiveness_criteria}`
            : null,
          record.effectiveness_verified ? 'Verified: Yes' : 'Verified: No',
          record.effectiveness_rating ? `Rating: ${record.effectiveness_rating}` : null,
          dash(record.effectiveness_notes),
        ]
          .filter(Boolean)
          .join(' · '),
      },
    },
    {
      lineNumber: 6,
      description: 'Closure',
      cells: {
        value:
          [
            record.closed_date ? `Closed: ${formatDate(record.closed_date)}` : null,
            record.closure_notes ? dash(record.closure_notes) : null,
          ]
            .filter(Boolean)
            .join(' · ') || '—',
      },
    },
  ].filter((line) => line.cells.value && line.cells.value !== '—');

  return (
    <EngineCommercialA4Print
      docType="capa"
      title="CORRECTIVE & PREVENTIVE ACTION (CAPA)"
      documentNumber={record.capa_number || 'CAPA'}
      partyName={dash(record.ncr?.ncr_number || record.source_reference)}
      introText={`${companyInfo.name} · CAPA · Systemic corrective / preventive action`}
      letterhead
      metaLines={metaLines}
      columns={[
        { key: 'sno', label: '#' },
        { key: 'description', label: 'Section' },
        { key: 'value', label: 'Detail' },
      ]}
      lines={
        narrativeLines.length
          ? narrativeLines
          : [
              {
                lineNumber: 1,
                description: 'Description',
                cells: { value: dash(record.description) },
              },
            ]
      }
      finals={[{ id: 'footer', heightPx: 48 }]}
      finalBlocks={{
        footer: (
          <div style={{ fontSize: 11, color: '#555' }}>
            CAPA is systemic CA/PA — distinct from NCR instance correction (disposition).
            {companyInfo.address ? ` · ${companyInfo.address}` : ''}
          </div>
        ),
      }}
    />
  );
}

export default CAPAPrintView;
